/**
 * Contract for the payload returned by Orders API getOrderList.
 */
export const ORDER_LIST_ROW_FIELDS = Object.freeze({
	PO_NUMBER: 'poNumber',
	STATUS: 'status',
	ORDER_DUE_DATE: 'orderDueDate',
	ITEM_DESCRIPTION: 'itemDescription',
	DESIGN_NUMBER: 'designNumber',
	CUSTOMER_ITEM_NUMBER: 'customerItemNumber',
	PLANT_NAME: 'plantName',
	ORDER_NUMBER: 'orderNumber',
	INVOICE_NUMBER: 'invoiceNumber',
	SHIPMENT_STATUS: 'shipmentStatus',
	SOURCE: 'source',
	ORDER_DATE: 'orderDate',
	PO_LINE_NUMBER: 'poLineNumber',
	QUANTITY_ORDERED: 'quantityOrdered',
	QUANTITY_SHIPPED: 'quantityShipped',
	QUANTITY_PRODUCED: 'quantityProduced',
	DIGITAL_DOCUMENTS: 'digitalDocuments',
	SHIPPING_INFO: 'shippingInfo',
	ORDER_STATUS_HISTORY: 'orderStatusHistory',
});

/**
 * Keys used by ShippingInfo records in Orders API responses.
 * Values mirror canonical API field names.
 */
export const ORDER_LIST_SHIPPING_FIELDS = Object.freeze({
	BOL: 'bol',
	SHIP_DATE: 'shipDate',
	INVOICE_NUMBER: 'invoiceNumbers',
	STATUS: 'status',
	CARRIER_EQUIPMENT_NUMBER: 'carrierEquipmentNumber',
});

export const ORDER_LIST_DOCUMENT_FIELDS = Object.freeze({
	ID: 'id',
	TYPE: 'type',
	FILE_NAME: 'fileName',
});

export const ORDER_LIST_STATUS_HISTORY_FIELDS = Object.freeze({
	BOL_NUMBER: 'bolNumber',
	ORDER_STATUS: 'orderStatus',
});

/**
 * Keys used by the top-level getOrderList response envelope.
 * Values mirror API field names.
 */
export const ORDER_LIST_RESPONSE_FIELDS = Object.freeze({
	COUNT: 'count',
	ORDERS: 'orders',
});

/**
 * Normalizes getOrderList response payload into a stable shape for UI consumers.
 *
 * @param {unknown} payload
 * @returns {OrdersListPayload}
 */
export const normalizeOrderListPayload = (payload) => {
	const data = payload && typeof payload === 'object' ? payload : {};

	return {
		[ORDER_LIST_RESPONSE_FIELDS.COUNT]:
			typeof data?.[ORDER_LIST_RESPONSE_FIELDS.COUNT] === 'number'
				? data[ORDER_LIST_RESPONSE_FIELDS.COUNT]
				: 0,
		[ORDER_LIST_RESPONSE_FIELDS.ORDERS]: Array.isArray(
			data?.[ORDER_LIST_RESPONSE_FIELDS.ORDERS]
		)
			? data[ORDER_LIST_RESPONSE_FIELDS.ORDERS]
			: [],
	};
};

/**
 * @typedef {Object} OrderListStatusHistory
 * @property {string} [bolNumber]
 * @property {string} [orderStatus]
 */

/**
 * @typedef {Object} OrderListShippingInfo
 * @property {string} [bol]
 * @property {string} [shipDate]
 * @property {string[]} [invoiceNumbers]
 * @property {string} [status]
 * @property {string} [carrierEquipmentNumber]
 */

/**
 * @typedef {Object} OrderListDocument
 * @property {string} id
 * @property {string} [type]
 * @property {string} [fileName]
 */

/**
 * @typedef {Object} OrderListRow
 * @property {string} [poNumber]
 * @property {string} [status]
 * @property {string} [orderDueDate]
 * @property {string} [itemDescription]
 * @property {string} [designNumber]
 * @property {string} [customerItemNumber]
 * @property {string} [plantName]
 * @property {string} [orderNumber]
 * @property {string} [invoiceNumber]
 * @property {string} [shipmentStatus]
 * @property {string} [source]
 * @property {string} [orderDate]
 * @property {string} [poLineNumber]
 * @property {number} [quantityOrdered]
 * @property {number} [quantityShipped]
 * @property {number} [quantityProduced]
 * @property {OrderListDocument[]} [digitalDocuments]
 * @property {OrderListShippingInfo[]} [shippingInfo]
 * @property {OrderListStatusHistory[]} [orderStatusHistory]
 */

/**
 * @typedef {Object} OrdersListPayload
 * @property {OrderListRow[]} orders
 * @property {number} count
 */
